class EfMobileNavigation extends HTMLElement {
  constructor() {
    super();

    this.layer = this.querySelector('[data-ef-nav-layer]');
    this.trigger = this.querySelector('[data-ef-nav-open]');
    this.panels = new Map(
      [...this.querySelectorAll('[data-panel-id]')].map((panel) => [
        panel.dataset.panelId,
        panel,
      ])
    );

    this.stack = ['root'];
    this.activePanelId = 'root';
    this.lastFocused = null;
    this.pageScrollY = 0;

    this.handleClick = this.onClick.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleViewportChange = this.updateViewportMetrics.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleViewportChange, { passive: true });
    window.addEventListener('orientationchange', this.handleViewportChange, { passive: true });
    window.visualViewport?.addEventListener('resize', this.handleViewportChange, { passive: true });
    window.visualViewport?.addEventListener('scroll', this.handleViewportChange, { passive: true });

    this.updateViewportMetrics();

    this.panels.forEach((panel, id) => {
      const isRoot = id === 'root';
      panel.hidden = !isRoot;
      panel.setAttribute('aria-hidden', isRoot ? 'false' : 'true');
      panel.classList.remove('is-entering-forward', 'is-entering-back');
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleViewportChange);
    window.removeEventListener('orientationchange', this.handleViewportChange);
    window.visualViewport?.removeEventListener('resize', this.handleViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.handleViewportChange);
  }

  onClick(event) {
    const openButton = event.target.closest('[data-ef-nav-open]');
    const closeButton = event.target.closest('[data-ef-nav-close]');
    const targetButton = event.target.closest('[data-ef-nav-target]');
    const backButton = event.target.closest('[data-ef-nav-back]');

    if (openButton) {
      event.preventDefault();
      this.open();
      return;
    }

    if (closeButton) {
      event.preventDefault();
      this.close();
      return;
    }

    if (targetButton) {
      event.preventDefault();
      this.goTo(targetButton.dataset.efNavTarget);
      return;
    }

    if (backButton) {
      event.preventDefault();
      this.goBack();
    }
  }

  onKeydown(event) {
    if (!this.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();

      if (this.stack.length > 1) {
        this.goBack();
      } else {
        this.close();
      }
    }
  }

  open() {
    if (this.classList.contains('is-open')) return;

    this.lastFocused = document.activeElement;
    this.updateViewportMetrics();
    this.layer.hidden = false;

    this.showPanel('root', 'none');
    this.lockPage();

    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.trigger?.setAttribute('aria-expanded', 'true');

      requestAnimationFrame(() => {
        this.getFocusableElement(this.panels.get('root'))?.focus();
      });
    });
  }

  close() {
    if (!this.classList.contains('is-open')) return;

    this.classList.remove('is-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.unlockPage();

    window.setTimeout(() => {
      this.layer.hidden = true;
      this.reset();
    }, 330);

    this.lastFocused?.focus?.();
  }

  goTo(id) {
    if (!id || !this.panels.has(id) || id === this.activePanelId) return;

    this.stack.push(id);
    this.showPanel(id, 'forward');
  }

  goBack() {
    if (this.stack.length <= 1) return;

    this.stack.pop();
    const previousId = this.stack[this.stack.length - 1];
    this.showPanel(previousId, 'back');
  }

  showPanel(id, direction = 'none') {
    const nextPanel = this.panels.get(id);
    if (!nextPanel) return;

    this.panels.forEach((panel, panelId) => {
      const isTarget = panelId === id;

      panel.hidden = !isTarget;
      panel.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
      panel.classList.remove('is-entering-forward', 'is-entering-back');
    });

    this.activePanelId = id;

    const scroller = nextPanel.querySelector('.ef-mobile-nav__panel-scroll');
    if (direction === 'forward' && scroller) {
      scroller.scrollTop = 0;
    }

    if (direction === 'forward') {
      nextPanel.classList.add('is-entering-forward');
    } else if (direction === 'back') {
      nextPanel.classList.add('is-entering-back');
    }

    const clearAnimationClass = () => {
      nextPanel.classList.remove('is-entering-forward', 'is-entering-back');
    };

    nextPanel.addEventListener('animationend', clearAnimationClass, { once: true });

    requestAnimationFrame(() => {
      this.getFocusableElement(nextPanel)?.focus();
    });
  }

  getFocusableElement(panel) {
    return panel?.querySelector(
      '[data-ef-nav-back], [data-ef-nav-close], a[href], button:not([disabled])'
    );
  }

  updateViewportMetrics() {
    const visualViewport = window.visualViewport;
    const visualHeight = Math.round(
      visualViewport?.height || window.innerHeight || document.documentElement.clientHeight
    );

    this.style.setProperty('--ef-mobile-nav-height', `${visualHeight}px`);
  }

  lockPage() {
    this.pageScrollY = window.scrollY || document.documentElement.scrollTop || 0;

    document.documentElement.classList.add('ef-mobile-nav-lock');
    document.body.classList.add('ef-mobile-nav-lock');

    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.pageScrollY}px`;
    document.body.style.width = '100%';
  }

  unlockPage() {
    document.documentElement.classList.remove('ef-mobile-nav-lock');
    document.body.classList.remove('ef-mobile-nav-lock');

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    window.scrollTo(0, this.pageScrollY);
  }

  reset() {
    this.stack = ['root'];
    this.activePanelId = 'root';

    this.panels.forEach((panel, id) => {
      const isRoot = id === 'root';

      panel.hidden = !isRoot;
      panel.setAttribute('aria-hidden', isRoot ? 'false' : 'true');
      panel.classList.remove('is-entering-forward', 'is-entering-back');

      const scroller = panel.querySelector('.ef-mobile-nav__panel-scroll');
      if (scroller) scroller.scrollTop = 0;
    });
  }
}

if (!customElements.get('ef-mobile-navigation')) {
  customElements.define('ef-mobile-navigation', EfMobileNavigation);
}
